import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { OKTA_REDIRECT_URL, STORAGE_KEY } from "@/lib/auth/constants";
import { decodeJwt, exchangeWebToken, saveTokenData } from "@/lib/auth/helpers";

interface CallbackSearch {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: typeof search.code === "string" ? search.code : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    error_description:
      typeof search.error_description === "string"
        ? search.error_description
        : undefined,
  }),
  component: CallbackPage,
});

function CallbackPage() {
  const search = Route.useSearch();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const exchangeAttempted = useRef(false);

  const {
    code,
    state,
    error: errorParam,
    error_description: errorDesc,
  } = search;

  useEffect(() => {
    if (exchangeAttempted.current || errorParam || !code) return;
    exchangeAttempted.current = true;

    async function exchange() {
      const expectedState = sessionStorage.getItem(STORAGE_KEY.OAUTH_STATE);

      if (!state || !expectedState || state !== expectedState) {
        sessionStorage.removeItem(STORAGE_KEY.CODE_VERIFIER);
        sessionStorage.removeItem(STORAGE_KEY.OAUTH_STATE);
        setError("Invalid OAuth state — please log in again");
        return;
      }

      const codeVerifier = sessionStorage.getItem(STORAGE_KEY.CODE_VERIFIER);

      if (!codeVerifier) {
        setError("Session expired — please log in again");
        return;
      }

      try {
        const { access_token } = await exchangeWebToken(
          code!,
          OKTA_REDIRECT_URL,
          codeVerifier,
        );

        sessionStorage.removeItem(STORAGE_KEY.CODE_VERIFIER);
        sessionStorage.removeItem(STORAGE_KEY.OAUTH_STATE);
        const claims = decodeJwt(access_token);
        saveTokenData({ accessToken: access_token, claims });
        setSuccess(true);

        let returnTo =
          localStorage.getItem(STORAGE_KEY.LOCATION_BEFORE_AUTH) || "/";
        localStorage.removeItem(STORAGE_KEY.LOCATION_BEFORE_AUTH);
        if (
          returnTo.startsWith("/login") ||
          returnTo.startsWith("/logout") ||
          returnTo.startsWith("/auth/")
        ) {
          returnTo = "/";
        }

        setTimeout(() => {
          window.location.href = returnTo;
        }, 1000);
      } catch (err) {
        sessionStorage.removeItem(STORAGE_KEY.CODE_VERIFIER);
        sessionStorage.removeItem(STORAGE_KEY.OAUTH_STATE);
        setError(err instanceof Error ? err.message : "Token exchange failed");
      }
    }

    void exchange();
  }, [code, state, errorParam]);

  const displayError = errorParam ? errorDesc || errorParam : error;

  return (
    <div className="flex min-h-screen items-center justify-center">
      {success ? (
        <p className="text-muted-foreground text-sm">
          Sign in successful! Redirecting…
        </p>
      ) : !code && !errorParam ? (
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive text-sm">Missing authorization code</p>
          <a
            href="/login"
            className="text-primary text-sm font-medium hover:underline"
          >
            Try Again
          </a>
        </div>
      ) : displayError ? (
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive text-sm">{displayError}</p>
          <a
            href="/login"
            className="text-primary text-sm font-medium hover:underline"
          >
            Try Again
          </a>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Signing in…</p>
      )}
    </div>
  );
}
